import {
    createFeatureSelector,
    createSelector
} from '@ngrx/store';
import { IPreferenceState } from './preference-state';
import { StateSlice } from '../state-slice';
import { getCompany, getUser } from '../session/session-selectors';
import { getCompanyAuthentication, getUserAuthentication } from '../auth/auth-selectors';
import { getLocationEntities } from '../shared/entities/entities-selectors';
import { Company } from '@core/models/common/company';
import { User } from '@core/models/user/user';
import { AuthSession } from '@core/models/auth/auth-session';
import { Location } from '@core/models/location/location';
import { SessionPreference } from '@core/models/preference/session-preference';
import { UserCompanyPreference } from '@core/models/preference/user-company-preference';
import { UserPreference } from '@core/models/preference/user-preference';
import { CompanyPreference } from '@core/models/preference/company-preference';
import { LocationPreference } from '@core/models/preference/location-preference';
import { PreferenceGroupType } from '@core/models/preference/preference-group-type';

export const selectPreferenceState = createFeatureSelector<IPreferenceState>(StateSlice.PREFERENCE);

export const getPreferenceState = createSelector([
    selectPreferenceState
], (state: IPreferenceState) => state);

export const getUserPreferences = createSelector([
    selectPreferenceState
], (state: IPreferenceState) => state.user);

export const getCompanyPreferences = createSelector([
    selectPreferenceState
], (state: IPreferenceState) => state.company);

export const getUserPreference = createSelector([
    getUserPreferences,
    getUser
],
(preferences: { [userId: string]: UserPreference }, user: User) => {
    return (
        (user &&
            preferences &&
            preferences.hasOwnProperty(user.id) &&
            preferences[user.id]) ||
        null
    );
});

export const getCompanyPreference = createSelector(
    getCompanyPreferences, getCompany,
    (
        preferences: { [companyId: string]: CompanyPreference },
        company: Company
    ) => {
        return (
            (company &&
                preferences &&
                preferences.hasOwnProperty(company.id) &&
                preferences[company.id]) ||
            null
        );
    }
);

export const getPreference = createSelector(
    getCompany,
    getUser,
    getUserPreference,
    getUserAuthentication,
    getLocationEntities
,
(company: Company, user: User, preferences: UserPreference, userAuth: AuthSession, locations: { [id: string ]: Location}) => {

    let preference: SessionPreference = new SessionPreference();
    let companyPreference: UserCompanyPreference = null;

    if (company && preferences?.company?.hasOwnProperty(company.id)) {
        companyPreference = preferences.company[userAuth?.cad?.company_id];

        // Restore from session entity
        if (companyPreference) {
            companyPreference = {
                ...companyPreference,
                name: company.name,
                id: company.id,
                avatar_url: company.avatar_url
            };
        }

        // Define location preference
        let locationPreference: LocationPreference = null;
        let previousLocationPreference: LocationPreference = null;

        if (companyPreference) {
            if (companyPreference.previousLocationId) {
                if (
                    preferences &&
                    preferences.location &&
                    preferences.location.hasOwnProperty(
                        companyPreference.previousLocationId
                    )
                ) {
                    previousLocationPreference =
                        preferences.location[
                            companyPreference.previousLocationId
                        ];

                    // Restore from entities
                    if (
                        previousLocationPreference &&
                        locations &&
                        locations.hasOwnProperty(
                            previousLocationPreference.id
                        )
                    ) {
                        previousLocationPreference = {
                            ...previousLocationPreference,
                            name:
                                locations[previousLocationPreference.id]
                                    .name,
                            avatar_url: 
                                locations[previousLocationPreference.id]
                                    .avatar_url
                        };
                    }
                }
            }

            if (companyPreference.locationId) {
                if (
                    preferences &&
                    preferences.location &&
                    preferences.location.hasOwnProperty(
                        companyPreference.locationId
                    )
                ) {
                    locationPreference =
                        preferences.location[companyPreference.locationId];

                    // Restore from entities
                    if (
                        locationPreference &&
                        locations &&
                        locations.hasOwnProperty(locationPreference.id)
                    ) {
                        locationPreference = {
                            ...locationPreference,
                            name: locations[locationPreference.id].name,
                            avatar_url: 
                                locations[locationPreference.id]
                                    .avatar_url
                        };
                    }
                }
            }
        }

        preference = Object.assign(new SessionPreference(), {
            sort: (preferences && preferences.sort) || null,
            group: preferences?.group || PreferenceGroupType.WORKER,
            company: companyPreference,
            location: locationPreference,
            previousLocation: previousLocationPreference,
        });

        return preference;
    } else {
        return null;
    }

});

export const getLocationPreference = createSelector(
    [getPreference],
    (preference: SessionPreference) => {
        return preference && preference.location;
    }
);

export const getPreviousLocationPreference = createSelector(
    [getPreference],
    (preference: SessionPreference) => {
        return preference && preference.previousLocation;
    }
);

export const getIHDSitesPreference = createSelector(
    [getUserPreference],
    (userPreference: UserPreference) => userPreference?.ihdSites || []
)
